import { QueryOptions } from "@apollo/client/core";
import apollo from "../../clients/apollo";
import {
  CoinType,
  FollowSuggestion,
  FollowUsersOutput,
  GlobalLeaderboardType,
  InventoriesFilters,
  ListGamesInput,
  PaginatedQueryInput,
  Query,
  User,
  UserSubscription,
} from "../graphql";
import {
  getActivitiesGQL,
  getTotalActivitiesGQL,
} from "../queries/activitiesQueryGql";
import { gameSessionGql, gamesGQL, gameGQL } from "../queries/games";
import { globalLeaderboardsGQL } from "../queries/globalLeaderboard";
import {
  getListInventoriesGQL,
  getUserInventoriesGQL,
} from "../queries/inventories";
import { getUserNotificationsGQL } from "../queries/notificationQueryGql";
import {
  coinWalletsGQL,
  followSuggestionsGQL,
  getConfigGQL,
  getFollowerUsersGQL,
  getFollowingUsersGQL,
  getLeaderboardRankGQL,
  getUserB2bSubscriptionGQL,
  getUserProfileAppGQL,
  loginRewardsGQL,
  meGQL,
  userTransactionsGQL,
} from "../queries/users";

export interface IParamsTransaction {
  userId: string;
  coinTypes: CoinType[];
  limit: number;
  offset: number;
}
export interface IParamsInventories {
  filters?: Partial<InventoriesFilters>;
  page?: PaginatedQueryInput;
}

const apolloClient = apollo.getInstance();

export const getActivities = async (
  params: {
    category?: string;
    limit?: number;
    offset?: number;
  },
  options: Partial<QueryOptions> = {},
): Promise<Query["me"]["activities"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getActivitiesGQL,
    variables: { input: params },
  });

  return res.data.me.activities;
};

export const getUserProfileApp = async (
  userId: string,
  options: Partial<QueryOptions> = {},
): Promise<User> => {
  const res = await apolloClient.query({
    ...options,
    query: getUserProfileAppGQL,
    variables: {
      id: userId,
    },
  });

  if (res?.errors) {
    throw res?.errors[0];
  }

  return res.data.user;
};

export const getTotalNbActivities = async (
  params: {
    category?: string;
    limit?: number;
    offset?: number;
  },
  options: Partial<QueryOptions> = {},
): Promise<Query["me"]["activities"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getTotalActivitiesGQL,
    variables: { input: params },
  });

  return res.data.me.activities;
};

export const queryGames = async (
  options: Partial<QueryOptions> = {},
  params: Partial<ListGamesInput>,
): Promise<Query["games"]["items"]> => {
  const res = await apolloClient.query({
    ...options,
    query: gamesGQL,
    variables: {
      input: params,
    },
  });

  return res.data.games.items;
};
export const queryGame = async (
  gameId: string,
  options: Partial<QueryOptions> = {},
): Promise<Query["game"]> => {
  const res = await apolloClient.query({
    ...options,
    query: gameGQL,
    variables: { gameId },
  });

  return res.data.game;
};

export const getGlobalLeaderboards = async (params: {
  globalLeaderboardType?: GlobalLeaderboardType;
  leaderboardId?: string;
  page?: { limit?: number; offset?: number };
}): Promise<Query["leaderboard"]> => {
  const res = await apolloClient.query({
    query: globalLeaderboardsGQL,
    variables: { input: params },
  });

  return res.data.leaderboard;
};

export const fetchListInventories = async (
  options: Partial<QueryOptions> = {},
  params?: IParamsInventories,
): Promise<Query["inventories"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getListInventoriesGQL,
    variables: { input: params },
  });

  return res.data.inventories;
};

export const fetchUserInventories = async (
  options: Partial<QueryOptions> = {},
  params?: IParamsInventories,
): Promise<Query["me"]["inventories"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getUserInventoriesGQL,
    variables: { input: params },
  });

  return res?.data?.me?.inventories;
};

export const getUserNotifications = async (
  options: Partial<QueryOptions> = {},
  params: {
    userId: string;
    product: string;
  },
): Promise<Query["users"]["items"][0]["notifications"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getUserNotificationsGQL,
    variables: params,
  });

  return res.data.user.notifications;
};

export const queryMe = async (
  options: Partial<QueryOptions> = {},
): Promise<Query["me"]> => {
  const res = await apolloClient.query({
    ...options,
    query: meGQL,
  });

  return res.data.me;
};

export const queryUserTransactions = async (
  params: IParamsTransaction,
  options: Partial<QueryOptions> = {},
): Promise<Query["user"]> => {
  const res = await apolloClient.query({
    ...options,
    query: userTransactionsGQL,
    variables: {
      id: params?.userId,
      coinTypes: params.coinTypes,
      limit: params.limit,
      offset: params.offset,
    },
  });

  return res.data.user;
};

export const queryCoinWallets = async (
  options: Partial<QueryOptions> = {},
): Promise<Query["me"]> => {
  const res = await apolloClient.query({
    ...options,
    query: coinWalletsGQL,
  });

  return res.data.me;
};

export const getLeaderboardRank = async (
  userId: string,
  options: Partial<QueryOptions> = {},
): Promise<Query["user"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getLeaderboardRankGQL,
    variables: {
      id: userId,
    },
  });

  return res.data.user;
};

export const getLoginRewards = async (
  options: Partial<QueryOptions> = {},
): Promise<Query["loginRewards"]> => {
  const loginRewards = await apolloClient.query({
    ...options,
    query: loginRewardsGQL,
  });

  return loginRewards.data.loginRewards;
};
export const getFollowingUsers = async (
  userId: string,
  page: PaginatedQueryInput,
  options: Partial<QueryOptions> = {},
): Promise<FollowUsersOutput> => {
  const loginRewards = await apolloClient.query({
    ...options,
    query: getFollowingUsersGQL,
    variables: {
      id: userId,
      input: { page },
    },
  });

  return loginRewards.data.user.following;
};
export const getFollowerUsers = async (
  userId: string,
  page: PaginatedQueryInput,
  options: Partial<QueryOptions> = {},
): Promise<FollowUsersOutput> => {
  const loginRewards = await apolloClient.query({
    ...options,
    query: getFollowerUsersGQL,
    variables: {
      id: userId,
      input: { page },
    },
  });

  return loginRewards.data.user.followers;
};
export const getFollowSuggestions = async (
  options: Partial<QueryOptions> = {},
): Promise<Array<FollowSuggestion>> => {
  const loginRewards = await apolloClient.query({
    ...options,
    query: followSuggestionsGQL,
  });

  return loginRewards.data.followSuggestions;
};

export const getConfig = async (
  options: Partial<QueryOptions> = {},
): Promise<Query["config"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getConfigGQL,
  });

  return res.data.config;
};

export const getGameSession = async (
  id: string,
  options: Partial<QueryOptions> = {},
): Promise<Query["gameSession"]> => {
  const res = await apolloClient.query({
    ...options,
    query: gameSessionGql,
    variables: { input: { id } },
  });

  return res.data.gameSession;
};

export const getUserB2bSubscription = async (
  options: Partial<QueryOptions> = {},
): Promise<UserSubscription> => {
  const res = await apolloClient.query({
    ...options,
    query: getUserB2bSubscriptionGQL,
  });

  return res.data.me.subscription;
};
